// src/Profile.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// Prefer env var; fallback to your auth server
const API_BASE = (process.env.REACT_APP_API_BASE || "/api").replace(/\/+$/, "");

function decodeJwtSafe(token) {
  try {
    const base64 = token.split(".")[1];
    if (!base64) return null;
    const normalized = base64.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const json = atob(normalized);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function isJwtExpired(decoded, skewMs = 15000) {
  if (!decoded?.exp) return false;
  const expMs = decoded.exp * 1000;
  return Date.now() + skewMs >= expMs;
}

// --- Komponen Ikon Mata (Supaya kodingan rapi) ---
const EyeIcon = ({ show }) => (
  show ? (
    // Ikon Mata Terbuka (Show)
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
    </svg>
  ) : (
    // Ikon Mata Dicoret (Hide)
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
      <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
      <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
    </svg>
  )
);

export default function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // --- STATE PASSWORD ---
  const [currentPass, setCurrentPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [passMsg, setPassMsg] = useState("");
  const [passLoading, setPassLoading] = useState(false);

  // --- STATE TOGGLE MATA (VISIBILITY) ---
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const token = localStorage.getItem("token");
  const decoded = useMemo(() => (token ? decodeJwtSafe(token) : null), [token]);

  const signOut = useCallback(() => {
    const remembered = localStorage.getItem("rememberEmail");
    localStorage.clear();
    if (remembered) localStorage.setItem("rememberEmail", remembered);
    navigate("/");
  }, [navigate]);

  const loadProfile = useCallback(async (signal) => {
    setErr("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/me`, {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        signal,
      });

      if (res.status === 401) {
        setErr("Your session has expired. Please log in again.");
        signOut();
        return;
      }
      if (!res.ok) {
        throw new Error(`Failed to load profile (HTTP ${res.status})`);
      }

      const data = await res.json();
      setUser(data || null);

      if (data?.name) localStorage.setItem("name", data.name);
      if (data?.organization) localStorage.setItem("organization", data.organization);
      if (data?.email) localStorage.setItem("email", data.email);
    } catch (e) {
      if (e.name === "AbortError") return; 
      const msg =
        e?.message?.includes("Failed to fetch")
          ? `Failed to fetch ${API_BASE}/me. Check network, CORS, or mixed-content.`
          : e?.message || "Failed to load profile";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }, [token, signOut]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPassMsg("");

    if (newPass !== confirmPass) {
      setPassMsg("New Password dan Confirm Password tidak sama.");
      return;
    }

    if (!currentPass || !newPass) {
      setPassMsg("Mohon isi semua kolom password.");
      return;
    }
    
    setPassLoading(true);

    try {
      const res = await fetch(`${API_BASE}/change-password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ 
          id: user?.id, 
          currentPassword: currentPass, 
          newPassword: newPass          
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setPassMsg(`Password has been successfully changed.`);
        setCurrentPass("");
        setNewPass("");
        setConfirmPass("");
        // Reset visibility mata juga biar aman
        setShowCurrent(false);
        setShowNew(false);
        setShowConfirm(false);
      } else {
        setPassMsg(`Gagal: ${data.message || "Terjadi kesalahan"}`);
      }
    } catch (error) {
      setPassMsg("Error koneksi ke server");
    } finally {
      setPassLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      signOut();
      return;
    }
    if (isJwtExpired(decoded)) {
      setErr("Your session has expired. Please log in again.");
      signOut();
      return;
    }

    const ctrl = new AbortController();
    loadProfile(ctrl.signal);
    return () => ctrl.abort();
  }, [token, decoded, signOut, loadProfile]);

  const expText = useMemo(() => {
    if (!decoded?.exp) return null;
    const d = new Date(decoded.exp * 1000);
    return d.toLocaleString();
  }, [decoded]);

  if (loading) return <div className="p-3">Loading profile…</div>;

  if (err) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">{err}</div>
        <button className="btn btn-light btn-sm mt-2" onClick={signOut}>Go to Login</button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">No user data available.</div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="h4 mb-0">Account Details</h2>
        <button className="btn btn-outline-secondary btn-sm" onClick={signOut}>
          Sign Out
        </button>
      </div>

      {/* Profile Card */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <div className="form-text mb-1">Name</div>
              <div className="fw-semibold">{user.name || "-"}</div>
            </div>
            <div className="col-md-6">
              <div className="form-text mb-1">Email</div>
              <div className="fw-semibold">{user.email || "-"}</div>
            </div>
            <div className="col-md-6">
              <div className="form-text mb-1">Role</div>
              <div className="fw-semibold">{user.role || "-"}</div>
            </div>
            <div className="col-md-6">
              <div className="form-text mb-1">Organization</div>
              <div className="fw-semibold">{user.organization || "-"}</div>
            </div>
          </div>
          {expText && (
            <div className="mt-3 small text-muted">
              Session expires: <span className="fw-semibold">{expText}</span>
            </div>
          )}
        </div>
      </div>

      {/* --- FORM GANTI PASSWORD DENGAN TOMBOL MATA --- */}
      <div className="card shadow-sm">
        <div className="card-header bg-white py-3">
          <h5 className="h6 mb-0">Change Password</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleChangePassword}>
            
            {/* 1. Current Password */}
            <div className="mb-3">
              <label className="form-label small text-muted">Current Password</label>
              <div className="input-group">
                <input 
                  type={showCurrent ? "text" : "password"} 
                  className="form-control"
                  placeholder="Enter current password"
                  value={currentPass}
                  onChange={(e) => setCurrentPass(e.target.value)}
                  required
                />
                <button 
                  className="btn btn-outline-secondary" 
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                >
                  <EyeIcon show={showCurrent} />
                </button>
              </div>
            </div>

            {/* 2. New Password */}
            <div className="mb-3">
              <label className="form-label small text-muted">New Password</label>
              <div className="input-group">
                <input 
                  type={showNew ? "text" : "password"} 
                  className="form-control"
                  placeholder="Enter new password"
                  value={newPass}
                  onChange={(e) => setNewPass(e.target.value)}
                  required
                />
                <button 
                  className="btn btn-outline-secondary" 
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                >
                  <EyeIcon show={showNew} />
                </button>
              </div>
            </div>

            {/* 3. Confirm New Password */}
            <div className="mb-3">
              <label className="form-label small text-muted">Confirm New Password</label>
              <div className="input-group">
                <input 
                  type={showConfirm ? "text" : "password"} 
                  className={`form-control ${confirmPass && newPass !== confirmPass ? "is-invalid" : ""}`}
                  placeholder="Retype new password"
                  value={confirmPass}
                  onChange={(e) => setConfirmPass(e.target.value)}
                  required
                />
                <button 
                  className="btn btn-outline-secondary" 
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                >
                   <EyeIcon show={showConfirm} />
                </button>
                {/* Invalid feedback perlu diluar input-group atau diatur display-nya */}
              </div>
              {confirmPass && newPass !== confirmPass && (
                 <div className="text-danger small mt-1">Passwords do not match</div>
              )}
            </div>

            <button 
              type="submit" 
              className="btn btn-primary btn-sm"
              disabled={passLoading || (newPass !== confirmPass)}
            >
              {passLoading ? "Updating..." : "Update Password"}
            </button>
          </form>

          {passMsg && (
            <div className={`mt-3 alert ${passMsg.includes("Sukses") ? "alert-success" : "alert-danger"} py-2 small`}>
              {passMsg}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
